import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import Icon from './Icon.js';
import VinylCover from './VinylCover.js';

const THUMB = 56;
const MAIN_MAX = 340;

function NavButton({ dir, onClick, label }) {
  return (
    <button
      type="button"
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={label}
      style={{
        position: 'absolute',
        top: '50%',
        [dir === 'prev' ? 'left' : 'right']: 6,
        transform: 'translateY(-50%)',
        width: 34,
        height: 34,
        borderRadius: '50%',
        border: '0.5px solid rgba(255,255,255,0.2)',
        background: 'rgba(0,0,0,0.55)',
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1 }}>{dir === 'prev' ? '‹' : '›'}</span>
    </button>
  );
}

/**
 * Galería de fotos del anuncio (hasta 3): vista principal, miniaturas, lightbox.
 */
export default function ListingPhotoGallery({
  urls = [],
  alt = 'Fotos del anuncio',
  fallbackItem = null,
  mainMaxPx = MAIN_MAX,
}) {
  const gallery = useMemo(
    () => (Array.isArray(urls) ? urls.map(u => String(u || '').trim()).filter(Boolean) : []),
    [urls]
  );
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const count = gallery.length;
  const safeIdx = count > 0 ? Math.min(activeIdx, count - 1) : 0;
  const activeUrl = count > 0 ? gallery[safeIdx] : null;
  const hasMany = count > 1;

  const galleryKey = gallery.join('|');

  useEffect(() => {
    setActiveIdx(0);
  }, [galleryKey]);

  useEffect(() => {
    if (activeIdx >= count && count > 0) setActiveIdx(count - 1);
  }, [activeIdx, count]);

  const goPrev = useCallback(() => {
    if (count < 2) return;
    setActiveIdx(i => (i <= 0 ? count - 1 : i - 1));
  }, [count]);

  const goNext = useCallback(() => {
    if (count < 2) return;
    setActiveIdx(i => (i >= count - 1 ? 0 : i + 1));
  }, [count]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = e => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen, goPrev, goNext]);

  const mainFrameStyle = {
    width: `min(92vw, ${mainMaxPx}px)`,
    height: `min(92vw, ${mainMaxPx}px)`,
    maxHeight: mainMaxPx,
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
    background: '#0a0a12',
    position: 'relative',
  };

  return (
    <>
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 0 8px' }}>
          {activeUrl ? (
            <div style={mainFrameStyle}>
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                title="Ampliar foto"
                style={{
                  width: '100%',
                  height: '100%',
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'zoom-in',
                  display: 'block',
                }}
              >
                <img
                  src={activeUrl}
                  alt={alt}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                />
              </button>
              {hasMany ? (
                <>
                  <NavButton dir="prev" onClick={goPrev} label="Foto anterior" />
                  <NavButton dir="next" onClick={goNext} label="Foto siguiente" />
                  <span
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '3px 8px',
                      borderRadius: 8,
                      background: 'rgba(0,0,0,0.6)',
                      color: 'rgba(255,255,255,0.9)',
                      border: '0.5px solid rgba(255,255,255,0.15)',
                      pointerEvents: 'none',
                    }}
                  >
                    {safeIdx + 1} / {count}
                  </span>
                </>
              ) : null}
            </div>
          ) : (
            <div style={{ width: 220, height: 220, borderRadius: '50%' }}>
              <VinylCover item={fallbackItem} />
            </div>
          )}
        </div>

        {hasMany ? (
          <div
            role="tablist"
            aria-label="Miniaturas del anuncio"
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: 10,
              padding: '0 4px 4px',
            }}
          >
            {gallery.map((url, i) => {
              const sel = i === safeIdx;
              return (
                <button
                  key={`${url}-${i}`}
                  type="button"
                  role="tab"
                  aria-selected={sel}
                  aria-label={`Foto ${i + 1} de ${count}`}
                  onClick={() => setActiveIdx(i)}
                  style={{
                    flexShrink: 0,
                    width: THUMB,
                    height: THUMB,
                    padding: 0,
                    borderRadius: 10,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: `2px solid ${sel ? C.gold : 'rgba(255,255,255,0.12)'}`,
                    background: sel ? 'rgba(200,168,75,0.18)' : 'rgba(255,255,255,0.06)',
                    boxSizing: 'border-box',
                    opacity: sel ? 1 : 0.72,
                    transition: 'opacity .15s, border-color .15s',
                  }}
                >
                  <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {lightboxOpen && activeUrl ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Vista ampliada de la foto"
          onClick={() => setLightboxOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(5,5,10,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            aria-label="Cerrar"
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '0.5px solid rgba(255,255,255,0.2)',
              background: 'rgba(0,0,0,0.5)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="x" size={18} color="#fff" />
          </button>
          {hasMany ? (
            <>
              <NavButton dir="prev" onClick={goPrev} label="Foto anterior" />
              <NavButton dir="next" onClick={goNext} label="Foto siguiente" />
              <span
                style={{
                  position: 'absolute',
                  bottom: 20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                {safeIdx + 1} / {count}
              </span>
            </>
          ) : null}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: 'min(96vw, 900px)',
              maxHeight: 'min(88vh, 900px)',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={activeUrl}
              alt={alt}
              style={{ maxWidth: '100%', maxHeight: 'min(88vh, 900px)', objectFit: 'contain', borderRadius: 8 }}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
